import { EventEmitter, NgZone, OnDestroy, OnInit, Output, Input, Injectable, Inject, InjectionToken } from "@angular/core";
import moment from 'moment';
import { Subscription } from "rxjs";
import "../../util/string";
import { KpiService } from "../services/kpiService";
import { KpiServiceError, KpiServiceErrorType } from "../services/kpiServiceError";
import { BaseComponent } from "../../common/baseComponent";
import { INamedClass } from "../../common/iNamedClass";
import { LoggerService } from "../../common/logging/logger.service";

export const SUMMARY_DETAIL_SERVICE_TOKEN = new InjectionToken<any>('SummaryDetailService');
export const DETAIL_SERVICE_TOKEN = new InjectionToken<any>('DetailService');
export const LOAD_SUMMARY_TOKEN = new InjectionToken<boolean>('loadSummary');

@Injectable({
    providedIn: 'root'
})
export abstract class KpiServiceHostComponent extends BaseComponent implements OnDestroy, INamedClass {

    public abstract getClassName(): string;
    private cancelIntervalId: ReturnType<typeof setInterval>;

    constructor(zone: NgZone, protected logger: LoggerService) {
        super(zone);
        this.cancelIntervalId = setInterval(() => this.updateLastUpdatedText(), 60000);
    }

    private updateLastUpdatedText(): void {
        if (this.mLastUpdatedTime) {
            let test = moment().toISOString();
            let now = moment(test).tz("Etc/GMT");

            let diff = moment.duration(now.diff(moment(this.mLastUpdatedTime)));
            this.doAsyncUIChanges(() => {
                if (diff.days() > 0) {
                    this.setLastUpdatedText("+ 1d");
                } else {
                    if (diff.hours() > 0) {
                        this.setLastUpdatedText("{0} hrs {1} min".format(diff.hours().toString(), diff.minutes().toString()));
                    } else {
                        if (diff.minutes() > 0) {
                            this.setLastUpdatedText("{0} min".format(diff.minutes().toString()));
                        }
                        else {
                            this.setLastUpdatedText("0 min");
                        }
                    }
                }
            });
        } else {
            this.doAsyncUIChanges(() => {
                this.mLastUpdatedText = '';
            });
        }
    }

    private mLastUpdatedTime: Date;

    protected setLastUpdatedTime(time: Date) {
        this.mLastUpdatedTime = time;
        this.updateLastUpdatedText();
    }

    private mLastUpdatedText: string = "";

    public get lastUpdatedText(): string {
        return this.mLastUpdatedText;
    }

    private setLastUpdatedText(text: string) {
        this.mLastUpdatedText = text;
        this.lastUpdatedTextChanged.emit(text);
    }

    private mWarn: boolean = false;

    public get warn(): boolean {
        return this.mWarn;
    }

    protected setWarn(warn: boolean) {
        this.mWarn = warn;
        this.isWarn.emit(warn);
    }

    private mLoading: boolean = false;

    public get loading(): boolean {
        return this.mLoading;
    }

    protected setLoading(loading: boolean) {
        this.doAsyncUIChanges(() => {
            this.mLoading = loading;
            this.isLoading.emit(loading)
        });
    }

    @Output() isLoading = new EventEmitter<boolean>();
    @Output() isWarn = new EventEmitter<boolean>();
    @Output() lastUpdatedTextChanged = new EventEmitter<string>();

    public async refresh(event?: Event): Promise<void> {
        this.logger.traceActivity("KpiRequested-{0}".format(this.getClassName()));
        if (event) event.stopPropagation();
        return new Promise<void>((resolve, reject) => {
              setTimeout(() => this.updateSummary()
              .then(() => { resolve(); this.logger.debug("KpiRefresh-{0}".format(this.getClassName())); })
              .catch(() => { reject(); this.logger.error("KpiRefreshError-{0}".format(this.getClassName())); }));
        });
    }

    public async showDetail(event: Event): Promise<void> {
        this.logger.traceActivity("KpiDetailRequested-{0}".format(this.getClassName()));
        if (event) event.stopPropagation();
        return new Promise<void>((resolve, reject) => {
            setTimeout(() => this.updateDetail()
                .then(() => { resolve(); this.logger.debug("KpiDetailRefresh-{0}".format(this.getClassName())); })
                .catch(() => { reject(); this.logger.error("KpiDetailRefreshError-{0}".format(this.getClassName())); }));
        });
    }

    protected abstract resetSummary(): Promise<void>;
    protected abstract updateSummary(): Promise<void>;
    protected abstract updateDetail(): Promise<void>;

    public ngOnDestroy(): void {
        clearInterval(this.cancelIntervalId);
    }

    protected abstract clearCaches(): void;
}

@Injectable({
    providedIn: 'root'
})
export abstract class SingleSummaryDetailKpiServiceHost<TSummaryDetailService extends KpiService<TSummaryDetailData>, TSummaryDetailData, TProcessedSummaryData, TProcessedDetailData>
    extends KpiServiceHostComponent implements OnInit {
    private mSummaryData: TProcessedSummaryData;
    public get summaryData(): TProcessedSummaryData { return this.mSummaryData; };
    private summaryRequest: number = -1;
    private detailRequest: number = -1;
    private mDetailData: TProcessedDetailData;
    public get detailData(): TProcessedDetailData { return this.mDetailData; };
    private mSummaryDetailError: any;
    public get summaryError(): any { return this.mSummaryDetailError; };
    public get detailError(): any { return this.mSummaryDetailError; };
    private summaryDetailUpdatingSubscription: Subscription;
    private summaryDetailUpdateOkSubscription: Subscription;
    private summaryDetailUpdateKoSubscription: Subscription;
    protected get summaryDetailService(): TSummaryDetailService { return this.mSummaryDetailService; }
    @Input() public detailProcessingArgs: any = null;

    constructor(zone: NgZone, logger: LoggerService, @Inject(SUMMARY_DETAIL_SERVICE_TOKEN) protected mSummaryDetailService: TSummaryDetailService) {
        super(zone, logger);

        this.summaryDetailUpdatingSubscription = this.mSummaryDetailService.Updating.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                if (this.detailRequest != -1 && payload.requestId == this.detailRequest) {
                    this.setLoading(true);
                } else {
                    this.setLoading(true);
                }
            });
        });
        this.summaryDetailUpdateOkSubscription = this.mSummaryDetailService.UpdateOk.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                if (this.detailRequest == -1 || payload.requestId == this.detailRequest) {
                    this.setLoading(false);
                    this.setWarn(false);
                    this.mSummaryData = this.processSummaryData(payload.data, this.summaryRequest == payload.requestId);
                    this.mDetailData = this.processDetailData(payload.data, this.detailProcessingArgs);
                    this.detailProcessingArgs = null;
                    this.setLastUpdatedTime(this.extractUpdateTime(payload.data));
                    this.mSummaryDetailError = null;
                    this.summaryRequest = -1;
                    if (this.detailRequest != -1) {
                        this.detailRequest = -1;
                        this.performDetailAction();
                    }
                }
            });
        });
        this.summaryDetailUpdateKoSubscription = this.mSummaryDetailService.UpdateKo.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                if (this.detailRequest == -1 || payload.requestId == this.detailRequest) {
                    this.setLoading(false);
                    this.setWarn(false);
                    this.mSummaryData = null;
                    this.mDetailData = null;
                    this.setLastUpdatedTime(null);
                    this.mSummaryDetailError = this.processSummaryDetailError(payload.error);
                    this.summaryRequest = -1;
                    if (this.detailRequest != -1) {
                        this.detailRequest = -1;
                        this.performDetailAction();
                    }
                }
            });
        });
    }

    public ngOnInit(): void {
        if (this.mSummaryDetailService.isUpdating) {
            this.setLoading(true);
        } else {
            this.setLoading(false);
            this.refresh(null);
        }
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.summaryDetailUpdatingSubscription) this.summaryDetailUpdatingSubscription.unsubscribe();
        if (this.summaryDetailUpdateOkSubscription) this.summaryDetailUpdateOkSubscription.unsubscribe();
        if (this.summaryDetailUpdateKoSubscription) this.summaryDetailUpdateKoSubscription.unsubscribe();
    }

    protected abstract processSummaryData(data: TSummaryDetailData, updateRequested: boolean): TProcessedSummaryData;
    protected abstract extractUpdateTime(data: TSummaryDetailData): Date;
    protected abstract processDetailData(data: TSummaryDetailData, detailProcessingArgs: any): TProcessedDetailData;
    protected processSummaryDetailError(error: KpiServiceError): Error {
        switch (error.type) {
            case KpiServiceErrorType.missingInputParameters: return new Error("No se pueden solicitar los datos.");
            case KpiServiceErrorType.noData: {
                this.setWarn(false);
                this.mSummaryData = this.getDefaultSummaryData();
                if (this.mSummaryData) this.setLastUpdatedTime(new Date());
                this.mDetailData = this.getDefaultDetailData();
                return null;
            }
            case KpiServiceErrorType.requestError: return new Error("No se pueden recuperar los datos.");
            default: return new Error(error.message);
        }
    }
    protected getDefaultSummaryData(): TProcessedSummaryData { return null; }
    protected getDefaultDetailData(): TProcessedDetailData { return null; }

    protected performDetailAction(): void { }

    protected resetSummary(): Promise<void> {
        this.mSummaryDetailService.clearCache();
        this.mSummaryDetailService.refresh(this.mSummaryDetailService.getRequestId());
        return Promise.resolve();
    }

    protected updateSummary(clearCache: boolean = false): Promise<void> {
        if (clearCache) this.mSummaryDetailService.clearCache();
        this.summaryRequest = this.mSummaryDetailService.getRequestId();
        this.mSummaryDetailService.refresh(this.summaryRequest);
        return Promise.resolve();
    }

    protected updateDetail(clearCache: boolean = false): Promise<void> {
        if (clearCache) this.mSummaryDetailService.clearCache();
        this.detailRequest = this.mSummaryDetailService.getRequestId();
        this.mSummaryDetailService.refresh(this.detailRequest);
        return Promise.resolve();
    }

    protected clearCaches() {
        this.mSummaryDetailService.clearCache();
    }

    protected refreshDataOnDetail(detailProcessingArgs: any): boolean {
        return true;
    }
}

@Injectable({
    providedIn: 'root'
})
export abstract class DualSummaryDetailKpiServiceHost<TSummaryService extends KpiService<TSummaryData>, TSummaryData, TProcessedSummaryData, TDetailService extends KpiService<TDetailData>, TDetailData, TProcessedDetailData>
    extends KpiServiceHostComponent implements OnInit {
    private mSummaryData: TProcessedSummaryData;
    public get summaryData(): TProcessedSummaryData { return this.mSummaryData; };
    private mSummaryError: any;
    public get summaryError(): any { return this.mSummaryError; };
    private summaryUpdatingSubscription: Subscription;
    private summaryUpdateOkSubscription: Subscription;
    private summaryUpdateKoSubscription: Subscription;

    private loadSummary: boolean = true;
    private summaryRequest: number = -1;
    private detailRequest: number = -1;
    private mDetailData: TProcessedDetailData;
    public get detailData(): TProcessedDetailData { return this.mDetailData; };
    private mDetailError: any;
    public get detailError(): any { return this.mDetailError; };
    private detailUpdatingSubscription: Subscription;
    private detailUpdateOkSubscription: Subscription;
    private detailUpdateKoSubscription: Subscription;
    protected get summaryService(): TSummaryService { return this.mSummaryService; }
    protected get detailService(): TDetailService { return this.mDetailService; }
    @Input() public detailProcessingArgs: any = null;

    constructor(zone: NgZone, logger: LoggerService, @Inject(SUMMARY_DETAIL_SERVICE_TOKEN) private mSummaryService: TSummaryService, @Inject(DETAIL_SERVICE_TOKEN) private mDetailService: TDetailService,
    @Inject(LOAD_SUMMARY_TOKEN) loadSummary: boolean = true) {
        super(zone, logger);

        this.loadSummary = loadSummary;

        this.summaryUpdatingSubscription = this.mSummaryService.Updating.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                this.setLoading(true);
            });
        });
        this.summaryUpdateOkSubscription = this.mSummaryService.UpdateOk.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                this.summaryRequest = -1;
                this.setLoading(this.detailRequest != -1);
                this.setWarn(false);
                this.mSummaryData = this.processSummaryData(payload.data, this.summaryRequest == payload.requestId);
                this.setLastUpdatedTime(this.extractUpdateTime(payload.data));
                this.mSummaryError = null;
            });
        });
        this.summaryUpdateKoSubscription = this.mSummaryService.UpdateKo.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                this.summaryRequest = -1;
                this.setLoading(this.detailRequest != -1);
                this.setWarn(false);
                this.mSummaryData = null;
                this.setLastUpdatedTime(null);
                this.mSummaryError = this.processSummaryError(payload.error);
            });
        });

        this.detailUpdatingSubscription = this.mDetailService.Updating.subscribe((payload) => {
            if (payload.requestId == this.detailRequest) {
                this.doAsyncUIChanges(() => {
                    this.setLoading(true);
                });
            }
        });
        this.detailUpdateOkSubscription = this.mDetailService.UpdateOk.subscribe((payload) => {
            if (payload.requestId == this.detailRequest) {
                this.doAsyncUIChanges(() => {
                    this.detailRequest = -1;
                    this.setLoading(this.summaryRequest != -1);
                    this.mDetailData = this.processDetailData(payload.data, this.detailProcessingArgs);
                    this.detailProcessingArgs = null;
                    this.mDetailError = null;
                    this.performDetailAction();
                });
            }
        });
        this.detailUpdateKoSubscription = this.mDetailService.UpdateKo.subscribe((payload) => {
            if (payload.requestId == this.detailRequest) {
                this.doAsyncUIChanges(() => {
                    this.detailRequest = -1;
                    this.setLoading(this.summaryRequest != -1);
                    this.mDetailData = null;
                    this.mDetailError = this.processDetailError(payload.error);
                    this.performDetailAction();
                });
            }
        });
    }

    public ngOnInit(): void {
        if (this.mSummaryService.isUpdating) {
            this.setLoading(true);
        } else {
            this.setLoading(false);
            this.refresh(null);
        }
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.summaryUpdatingSubscription) this.summaryUpdatingSubscription.unsubscribe();
        if (this.summaryUpdateOkSubscription) this.summaryUpdateOkSubscription.unsubscribe();
        if (this.summaryUpdateKoSubscription) this.summaryUpdateKoSubscription.unsubscribe();

        if (this.detailUpdatingSubscription) this.detailUpdatingSubscription.unsubscribe();
        if (this.detailUpdateOkSubscription) this.detailUpdateOkSubscription.unsubscribe();
        if (this.detailUpdateKoSubscription) this.detailUpdateKoSubscription.unsubscribe();
    }

    protected abstract processSummaryData(data: TSummaryData, updateRequested: boolean): TProcessedSummaryData;
    protected abstract extractUpdateTime(data: TSummaryData): Date;
    protected processSummaryError(error: KpiServiceError): Error {
        switch (error.type) {
            case KpiServiceErrorType.missingInputParameters: return new Error("No se pueden solicitar los datos.");
            case KpiServiceErrorType.noData: {
                this.setWarn(false);
                this.mSummaryData = this.getDefaultSummaryData();
                if (this.mSummaryData) this.setLastUpdatedTime(new Date());
                this.mDetailData = this.getDefaultDetailData();
                return null;
            }
            case KpiServiceErrorType.requestError: return new Error("No se pueden recuperar los datos.");
            default: return new Error(error.message);
        }
    }

    protected abstract processDetailData(data: TDetailData, detailProcessingArgs: any): TProcessedDetailData;
    protected processDetailError(error: KpiServiceError): Error {
        switch (error.type) {
            case KpiServiceErrorType.missingInputParameters: return new Error("No se pueden solicitar los datos.");
            case KpiServiceErrorType.noData: {
                this.setWarn(false);
                this.mSummaryData = this.getDefaultSummaryData();
                if (this.mSummaryData) this.setLastUpdatedTime(new Date());
                this.mDetailData = this.getDefaultDetailData();
                return null;
            }
            case KpiServiceErrorType.requestError: return new Error("No se pueden recuperar los datos.");
            default: return new Error(error.message);
        }
    }

    protected getDefaultSummaryData(): TProcessedSummaryData { return null; }
    protected getDefaultDetailData(): TProcessedDetailData { return null; }

    protected performDetailAction(): void { }

    protected resetSummary(): Promise<void> {
        this.mSummaryService.clearCache();
        this.mSummaryService.refresh(this.mSummaryService.getRequestId());
        return Promise.resolve();
    }

    protected updateSummary(clearCache: boolean = false): Promise<void> {
      if (this.loadSummary) {
        if (clearCache) this.mSummaryService.clearCache();
        this.summaryRequest = this.mSummaryService.getRequestId();
        this.mSummaryService.refresh(this.summaryRequest);
        return Promise.resolve();
      } else {
        return Promise.resolve();
      }
    }

    protected updateDetail(clearCache: boolean = false): Promise<void> {
        //update summary to try to minimize summary detail mismatch, differences may still arise due to cache operation
        clearCache = clearCache || this.summaryService.cacheExpired() || this.detailService.cacheExpired();
        this.updateSummary(clearCache);
        if (clearCache) this.mDetailService.clearCache();
        this.detailRequest = this.mDetailService.getRequestId();
        this.mDetailService.refresh(this.detailRequest);
        return Promise.resolve();
    }

    protected clearCaches() {
        this.mSummaryService.clearCache();
        this.mDetailService.clearCache();
    }
}

@Injectable({
    providedIn: 'root'
})
export abstract class TableDetailKpiServiceHost<TSummaryDetailService extends KpiService<TSummaryDetailData>, TSummaryDetailData> extends KpiServiceHostComponent
    implements OnInit {
    private summaryRequest: number = -1;
    private detailRequest: number = -1;

    private mSummaryData: TSummaryDetailData;
    public get summaryData(): TSummaryDetailData { return this.mSummaryData; };


    private mSummaryDetailError: any;
    public get summaryError(): any { return this.mSummaryDetailError; };
    public get detailError(): any { return this.mSummaryDetailError; };

    private summaryDetailUpdatingSubscription: Subscription;
    private summaryDetailUpdateOkSubscription: Subscription;
    private summaryDetailUpdateKoSubscription: Subscription;

    protected get summaryDetailService(): TSummaryDetailService { return this.mSummaryDetailService; }

    @Input() public detailProcessingArgs: any = null;

    constructor(zone: NgZone, logger: LoggerService, @Inject(SUMMARY_DETAIL_SERVICE_TOKEN) protected mSummaryDetailService: TSummaryDetailService) {
        super(zone, logger);

        this.summaryDetailUpdatingSubscription = this.mSummaryDetailService.Updating.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                if (this.detailRequest != -1 && payload.requestId == this.detailRequest) {
                    this.setLoading(true);
                } else {
                    this.setLoading(true);
                }
            });
        });
        this.summaryDetailUpdateOkSubscription = this.mSummaryDetailService.UpdateOk.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                if (this.detailRequest == -1 || payload.requestId == this.detailRequest) {
                    this.setLoading(false);
                    this.setWarn(false);
                    this.mSummaryData = this.processSummaryData(payload.data, this.summaryRequest == payload.requestId);
                    this.detailProcessingArgs = null;
                    this.setLastUpdatedTime(this.extractUpdateTime(payload.data));
                    this.mSummaryDetailError = null;
                    this.summaryRequest = -1;
                    if (this.detailRequest != -1) {
                        this.detailRequest = -1;
                        this.performDetailAction();
                    }
                }
            });
        });
        this.summaryDetailUpdateKoSubscription = this.mSummaryDetailService.UpdateKo.subscribe((payload) => {
            this.doAsyncUIChanges(() => {
                if (this.detailRequest == -1 || payload.requestId == this.detailRequest) {
                    this.setLoading(false);
                    this.setWarn(false);
                    this.mSummaryData = null;
                    this.setLastUpdatedTime(null);
                    this.mSummaryDetailError = this.processSummaryDetailError(payload.error);
                    this.summaryRequest = -1;
                    if (this.detailRequest != -1) {
                        this.detailRequest = -1;
                        this.performDetailAction();
                    }
                }
            });
        });
    }

    public ngOnInit(): void {
        if (this.mSummaryDetailService.isUpdating) {
            this.setLoading(true);
        } else {
            this.setLoading(false);
            this.refresh(null);
        }
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.summaryDetailUpdatingSubscription) this.summaryDetailUpdatingSubscription.unsubscribe();
        if (this.summaryDetailUpdateOkSubscription) this.summaryDetailUpdateOkSubscription.unsubscribe();
        if (this.summaryDetailUpdateKoSubscription) this.summaryDetailUpdateKoSubscription.unsubscribe();
    }

    protected abstract processSummaryData(data: TSummaryDetailData, updateRequested: boolean);
    protected abstract extractUpdateTime(data: TSummaryDetailData): Date;

    protected processSummaryDetailError(error: KpiServiceError): Error {
        switch (error.type) {
            case KpiServiceErrorType.missingInputParameters: return new Error("No se pueden solicitar los datos.");
            case KpiServiceErrorType.noData: {
                this.setWarn(false);
                this.mSummaryData = this.getDefaultSummaryData();
                if (this.mSummaryData) this.setLastUpdatedTime(new Date());

                return null;
            }
            case KpiServiceErrorType.requestError: return new Error("No se pueden recuperar los datos.");
            default: return new Error(error.message);
        }
    }
    protected getDefaultSummaryData(): TSummaryDetailData { return null; }

    protected performDetailAction(): void { }

    protected resetSummary(): Promise<void> {
        this.mSummaryDetailService.clearCache();
        this.mSummaryDetailService.refresh(this.mSummaryDetailService.getRequestId());
        return Promise.resolve();
    }

    protected updateSummary(clearCache: boolean = false): Promise<void> {
        if (clearCache) this.mSummaryDetailService.clearCache();
        this.summaryRequest = this.mSummaryDetailService.getRequestId();
        this.mSummaryDetailService.refresh(this.summaryRequest);
        return Promise.resolve();
    }

    protected updateDetail(clearCache: boolean = false): Promise<void> {
        if (clearCache) this.mSummaryDetailService.clearCache();
        this.detailRequest = this.mSummaryDetailService.getRequestId();
        this.mSummaryDetailService.refresh(this.detailRequest);
        return Promise.resolve();
    }

    protected clearCaches() {
        this.mSummaryDetailService.clearCache();
    }

    protected refreshDataOnDetail(detailProcessingArgs: any): boolean {
        return true;
    }
}
